import { NetworkStatus, type TypedDocumentNode } from "@apollo/client";
import type { DocumentNode, OperationVariables } from "@apollo/client";
import { canonicalStringify } from "@apollo/client/utilities";
import { useApolloClient, useQuery } from "@apollo/client/react";
import _ from "lodash";
import {
  ComponentProps,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CalendarDaysIcon, SlidersHorizontalIcon } from "lucide-react";

import {
  FilterDefinitionType,
  FilterType,
  SortableType,
} from "@/components/@types";
import { TableDialog } from "@/components/organisms/Table/TableDialog";
import {
  FILTER_OPERATORS_FOR_BOOLEANS,
  FILTER_OPERATORS_FOR_DATES,
  FILTER_OPERATORS_FOR_ENUMS,
  FILTER_OPERATORS_FOR_NUMBERS,
  FILTER_OPERATORS_FOR_REFERENCES,
  FILTER_OPERATORS_FOR_STRINGS,
} from "@/components/organisms/UtilityBar/FilterChip/FilterChip.content";
import {
  columnsToFilterDefinitions,
  columnsToSortableDefinitions,
} from "@/components/organisms/UtilityBar/UtilityBar.utils";
import {
  BulkOperationEnum,
  ContactEnum,
  DocumentEnum,
  FinancialAccountTypeEnum,
} from "@/__generated__/graphql";
import { displayNameForView } from "@/app/app/_/commands/NavigateAppViewCommands";
import type { SelectionScope } from "@/components/molecules/SelectableListSelectionBar";
import { Mutations, Queries } from "@/constants";
import { removeNodesFromConnections } from "@/app/_/subscriptions/writeObjectChangeToCache";
import {
  buildOrmFilterBranch,
  ormFilterPlacement,
  SEARCH_FILTER_KEY,
  sortableToOrmOrder,
} from "@/graphql/filters";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { Objects } from "@/objects";
import { toColumns } from "@/objects/@utils";
import { useAppRouteState } from "@/app/app/_/routing";
import { State } from "@/state";
import { AppViewsType } from "@/state/@types";

/**
 * Default for ``minLoadingMs``: how long the skeleton is shown before real
 * rows, on mount and on every variables change — including when the rows are
 * already cached.
 */
const DEFAULT_MIN_LOADING_MS = 500;

// =============================================================================
// Connection views — what each list view fetches
// =============================================================================
/** Tab-locked filters a view applies, per tab. */
export type TabOnChangeMap = {
  when: { type: "tab"; value: string };
  set: { type: "filter"; value: FilterType[] };
}[];

export type ConnectionViewConfig = {
  query: TypedDocumentNode<unknown>;
  /** Key the connection sits under in the query result. */
  queryKey: string;
  typename: keyof typeof Objects;
  onChangeMap: TabOnChangeMap;
  /**
   * Whether this view's rows can be searched — the ledger views, which is
   * where the search bar appears.
   *
   * It tracks one backend fact: the model's service sets
   * ``is_searchable = True``, so its rows reach the ``SearchIndex`` and its
   * ORM filter input carries the ranked ``search`` field (via
   * ``SearchFilterMixin``). Reports and rules have neither, so asking them
   * for ``search`` is a GraphQL error, not an empty list.
   */
  searchable?: boolean;
  /**
   * Records of another kind the list's rows can be filtered by, keyed by the
   * relation the API's filter reaches them through (``object: { documents }``
   * on ``TransactionFilter``): the related object, and which of its filter
   * columns are offered - those its own filter input answers. Its ``self``
   * column is the relation's own chip: which record.
   */
  relatedFilters?: Record<
    string,
    { typename: keyof typeof Objects; keys: string[] }
  >;
  /**
   * Filters no column offers, by the key the API's filter input takes
   * (``isLineItem`` on ``TransactionFilter``): what they are called, and what
   * they are asked with.
   */
  filters?: Record<string, { name: string; type: "boolean" }>;
};

/**
 * Every list view's connection query, in one place.
 *
 * These used to live inline in each ``*View/index.tsx``, which meant the only
 * way to learn what a view fetches was to mount it. The sidebar needs that
 * answer *before* navigating so it can start the query on hover, so the
 * configs moved here and ``useConnectionSetup`` reads them by view — one
 * source of truth, so a preload cannot drift from what the view asks for.
 * Shaped like ``nodePanelConfig``: a small map keyed by ``AppViewsType``.
 */
export const CONNECTION_VIEWS = {
  transactions: {
    query: Queries.TRANSACTIONS_CONNECTION_QUERY,
    queryKey: "transactionsConnection",
    typename: "TransactionObject",
    searchable: true,
    // Whether it is a line of any document - off, the transactions a line of
    // none.
    filters: {
      isLineItem: { name: "Line item", type: "boolean" },
    },
    // The documents a transaction is on: a bank statement's lines, an
    // invoice's payment.
    relatedFilters: {
      documents: {
        typename: "DocumentObject",
        keys: [
          "self",
          "type",
          "contact",
          "name",
          "reference",
          "amountTotal",
          "createdAt",
        ],
      },
    },
    onChangeMap: [
      {
        when: { type: "tab", value: "bank-feed" },
        set: {
          type: "filter",
          value: [
            {
              key: "financialAccount",
              type: "reference",
              operator: "isNull",
              value: false,
              isLocked: true,
            },
          ],
        },
      },
      {
        when: { type: "tab", value: "posted" },
        set: {
          type: "filter",
          value: [
            {
              key: "posted",
              type: "boolean",
              operator: "exact",
              value: true,
              isLocked: true,
            },
          ],
        },
      },
      {
        when: { type: "tab", value: "unposted" },
        set: {
          type: "filter",
          value: [
            {
              key: "posted",
              type: "boolean",
              operator: "exact",
              value: false,
              isLocked: true,
            },
          ],
        },
      },
    ],
  },
  documents: {
    query: Queries.DOCUMENTS_CONNECTION_QUERY,
    queryKey: "documentsConnection",
    typename: "DocumentObject",
    searchable: true,
    onChangeMap: [
      {
        when: { type: "tab", value: "invoices" },
        set: {
          type: "filter",
          value: [
            {
              key: "type",
              type: "enum",
              operator: "exact",
              value: DocumentEnum.Invoice,
              isLocked: true,
            },
          ],
        },
      },
      {
        when: { type: "tab", value: "unpaid-invoices" },
        set: {
          type: "filter",
          value: [
            {
              key: "type",
              type: "enum",
              operator: "exact",
              value: DocumentEnum.Invoice,
              isLocked: true,
            },
            {
              key: "unpaidOnly",
              type: "boolean",
              operator: "exact",
              value: true,
              isLocked: true,
            },
          ],
        },
      },
      {
        when: { type: "tab", value: "bills" },
        set: {
          type: "filter",
          value: [
            {
              key: "type",
              type: "enum",
              operator: "exact",
              value: DocumentEnum.Bill,
              isLocked: true,
            },
          ],
        },
      },
      {
        when: { type: "tab", value: "unreconciled" },
        set: {
          type: "filter",
          value: [
            {
              key: "awaitingBankMatch",
              type: "boolean",
              operator: "exact",
              value: true,
              isLocked: true,
            },
          ],
        },
      },
    ],
  },
  contacts: {
    query: Queries.PEOPLES_CONNECTION_QUERY,
    queryKey: "contactsConnection",
    typename: "ContactObject",
    searchable: true,
    onChangeMap: [
      {
        when: { type: "tab", value: "customers" },
        set: {
          type: "filter",
          value: [
            {
              key: "type",
              type: "enum",
              operator: "exact",
              value: ContactEnum.Customer,
              isLocked: true,
            },
          ],
        },
      },
      {
        when: { type: "tab", value: "employees" },
        set: {
          type: "filter",
          value: [
            {
              key: "type",
              type: "enum",
              operator: "exact",
              value: ContactEnum.Employee,
              isLocked: true,
            },
          ],
        },
      },
      {
        when: { type: "tab", value: "suppliers" },
        set: {
          type: "filter",
          value: [
            {
              key: "type",
              type: "enum",
              operator: "exact",
              value: ContactEnum.Supplier,
              isLocked: true,
            },
          ],
        },
      },
    ],
  },
  organizations: {
    query: Queries.ORGANIZATIONS_CONNECTION_QUERY,
    queryKey: "organizationsConnection",
    typename: "OrganizationObject",
    searchable: true,
    onChangeMap: [],
  },
  "financial-accounts": {
    query: Queries.FINANCIAL_ACCOUNTS_CONNECTION_QUERY,
    queryKey: "financialAccountsConnection",
    typename: "FinancialAccountObject",
    searchable: true,
    // Debit: the accounts that hold money (chequing, savings). Credit: what is
    // owed on a card. Loans and investments are in all of them.
    onChangeMap: [
      {
        when: { type: "tab", value: "debit" },
        set: {
          type: "filter",
          value: [
            {
              key: "type",
              type: "enum",
              operator: "exact",
              value: FinancialAccountTypeEnum.Depository,
              isLocked: true,
            },
          ],
        },
      },
      {
        when: { type: "tab", value: "credit" },
        set: {
          type: "filter",
          value: [
            {
              key: "type",
              type: "enum",
              operator: "exact",
              value: FinancialAccountTypeEnum.Credit,
              isLocked: true,
            },
          ],
        },
      },
    ],
  },
  "journal-entries": {
    query: Queries.JOURNAL_ENTRIES_CONNECTION_QUERY,
    queryKey: "journalEntriesConnection",
    typename: "JournalEntryObject",
    searchable: true,
    onChangeMap: [],
  },
  "tax-codes": {
    query: Queries.TAXES_CONNECTION_QUERY,
    queryKey: "taxesConnection",
    typename: "TaxObject",
    searchable: true,
    onChangeMap: [],
  },
  reports: {
    query: Queries.REPORTS_CONNECTION_QUERY,
    queryKey: "reportsConnection",
    typename: "ReportObject",
    onChangeMap: [],
  },
  rules: {
    query: Queries.RULES_CONNECTION_QUERY,
    queryKey: "rulesConnection",
    typename: "RuleDefinitionObject",
    onChangeMap: [],
  },
} satisfies Partial<Record<AppViewsType, ConnectionViewConfig>>;

export type ConnectionViewType = keyof typeof CONNECTION_VIEWS;

export function isConnectionView(
  view: AppViewsType,
): view is ConnectionViewType {
  return view in CONNECTION_VIEWS;
}

// =============================================================================
// Fetch freshness — how long a fetched query stays servable from the cache
// =============================================================================
/** How long a fetched query stays servable from the cache. */
export const FRESHNESS_WINDOW_MS = 120_000;

/**
 * When each (query, variables) pair last came back from the network.
 *
 * Query documents are module constants, so a ``WeakMap`` keyed on the document
 * is stable and self-clearing; variables are keyed with Apollo's own
 * ``canonicalStringify`` so a preload and the view that consumes it agree on
 * identity the same way the cache does.
 */
const lastFetchedAt = new WeakMap<DocumentNode, Map<string, number>>();

export function markFetched(
  query: DocumentNode,
  variables: OperationVariables,
) {
  let byVariables = lastFetchedAt.get(query);
  if (!byVariables) {
    byVariables = new Map();
    lastFetchedAt.set(query, byVariables);
  }
  byVariables.set(canonicalStringify(variables), Date.now());
}

/**
 * Was this exact query fetched within the freshness window?
 *
 * Callers use it to pick ``cache-first`` over ``network-only``: inside the
 * window a sidebar hover has already put the result in the cache, so the click
 * can render it; past it the data is stale and goes back to the network.
 * Always ``cache-first`` rather than ``cache-only``, so a read that misses
 * still falls through to the network and the worst case is today's behaviour.
 */
export function isFresh(
  query: DocumentNode,
  variables: OperationVariables,
): boolean {
  const at = lastFetchedAt.get(query)?.get(canonicalStringify(variables));
  return at != null && Date.now() - at < FRESHNESS_WINDOW_MS;
}

const OPERATORS_BY_TYPE: Record<string, FilterDefinitionType["operators"]> = {
  text: FILTER_OPERATORS_FOR_STRINGS,
  number: FILTER_OPERATORS_FOR_NUMBERS,
  currency: FILTER_OPERATORS_FOR_NUMBERS,
  reference: FILTER_OPERATORS_FOR_REFERENCES,
  enum: FILTER_OPERATORS_FOR_ENUMS,
  enums: FILTER_OPERATORS_FOR_ENUMS,
  boolean: FILTER_OPERATORS_FOR_BOOLEANS,
  date: FILTER_OPERATORS_FOR_DATES,
};

function keyToLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/**
 * Filter definitions a view understands: one per column, any key a tab
 * locks that no column exposes (``awaitingBankMatch``, ``posted``), and one
 * per related record's column it offers (``documents.type``, headed by the
 * record).
 */
function connectionFilterDefinitions(
  config: ConnectionViewConfig,
): Record<string, FilterDefinitionType> {
  const definitions = columnsToFilterDefinitions(
    toColumns(Objects[config.typename]),
  );

  for (const entry of config.onChangeMap) {
    for (const filter of entry.set.value) {
      if (!definitions[filter.key]) {
        definitions[filter.key] = {
          type: filter.type as FilterDefinitionType["type"],
          name: keyToLabel(filter.key),
          operators: OPERATORS_BY_TYPE[filter.type] ?? [],
          ReactIcon: SlidersHorizontalIcon,
        };
      }
    }
  }

  // When a row was made, on every list - a filter only, drawn nowhere else. A
  // list that shows it as a column already offers it as one (a document's
  // "Date").
  definitions.createdAt ??= {
    type: "date",
    name: "Created at",
    operators: OPERATORS_BY_TYPE.date,
    ReactIcon: CalendarDaysIcon,
  };

  for (const [key, { name, type }] of Object.entries(config.filters ?? {})) {
    definitions[key] = {
      type,
      name,
      operators: OPERATORS_BY_TYPE[type] ?? [],
      ReactIcon: SlidersHorizontalIcon,
    };
  }

  // Last: a heading groups every filter after it.
  for (const [relation, { typename, keys }] of Object.entries(
    config.relatedFilters ?? {},
  )) {
    const related = columnsToFilterDefinitions(toColumns(Objects[typename]));
    const label = related.self?.name ?? keyToLabel(relation);
    for (const key of keys) {
      const definition = related[key];
      if (!definition) {
        continue;
      }
      definitions[key === "self" ? relation : `${relation}.${key}`] = {
        ...definition,
        name:
          // A path to it: "Document → Type".
          key === "self" ? label : `${label} → ${definition.name}`,
        group: label,
      };
    }
  }

  return definitions;
}

/**
 * The query a sidebar item's destination will run, for ``useHoverPrefetch``.
 *
 * Sidebar navigation always lands on the view's default tab with filters and
 * sort cleared (``preserveQuery: false`` in ``navigateToView``), so these
 * variables are derivable without mounting the view. They must match the
 * view's exactly — a single differing key is a different operation, so Apollo
 * would run it separately instead of deduplicating the click onto it.
 */
export function connectionPrefetchDescriptor(
  view: AppViewsType,
  tab: string | null,
) {
  if (!isConnectionView(view)) {
    return null;
  }
  // Never preload what is already on screen. The preload writes to the cache
  // so that a result which lands before the click is still there when it
  // comes, but that write broadcasts to every mounted query watching the same
  // entry — so preloading the open view would repaint the table under a user
  // who is only moving the mouse. There is nothing to gain there anyway.
  const state = State.getState();
  if (view === state.activeAppView && tab === state.activeTab) {
    return null;
  }
  return {
    query: (CONNECTION_VIEWS[view] as ConnectionViewConfig).query,
    variables: connectionVariables(view, {
      tab,
      filters: [],
      sortables: [],
      entityIdentifiers: State.getState().activeEntityIdentifiers,
    }),
  };
}

/**
 * The variables *view*'s connection is asked with: its tab's filters, the
 * reader's chips and sort, over the entities in scope. The one set a list is
 * asked for - by its page, by the preload of its link, and by an export of it
 * - so each reads the same rows.
 */
export function connectionVariables(
  view: ConnectionViewType,
  {
    tab,
    filters,
    sortables,
    entityIdentifiers,
  }: {
    tab: string | null;
    filters: FilterType[];
    sortables: SortableType[];
    entityIdentifiers: string[];
  },
) {
  const config = CONNECTION_VIEWS[view] as ConnectionViewConfig;
  return {
    ...getQueryVariables({
      filterDefinitions: connectionFilterDefinitions(config),
      sortables,
      filters: resolveConnectionFilters({
        tab,
        onChangeMap: config.onChangeMap,
        filters,
      }),
      searchable: config.searchable,
    }),
    entityIdentifiers,
  };
}

/**
 * The ``filter`` and ``order`` a list is asked with for its chips and sort -
 * the list views', and a record's own list that a group's toolbar filters.
 */
export function getQueryVariables({
  filterDefinitions,
  sortables,
  filters,
  searchable = false,
}: {
  filterDefinitions: Record<string, FilterDefinitionType>;
  sortables: SortableType[];
  filters: FilterType[];
  searchable?: boolean;
}) {
  const branches: Record<string, unknown>[] = [];

  for (const filter of filters) {
    if (filter.value === null || filter.value === undefined) {
      continue;
    }
    // The search bar's term rides in ``filters`` but is not a chip: it is a
    // ranked index lookup on the ORM input's top-level ``search``, with no
    // column and so no definition for the operator handling below to find.
    // Dropped on a view without that field — a URL carrying a stale term
    // would otherwise make every query on it fail to validate.
    if (filter.key === SEARCH_FILTER_KEY) {
      const term = String(filter.value).trim();
      if (searchable && term) {
        branches.push(
          buildOrmFilterBranch(
            SEARCH_FILTER_KEY,
            { contains: term },
            "topLevel",
          ),
        );
      }
      continue;
    }
    const filterDefinition = filterDefinitions[filter.key];
    if (!filterDefinition) {
      continue;
    }
    const operator = _.flatten(filterDefinition.operators).find(
      (o) => o.key === filter.operator,
    );
    const lookup = operator?.getInputValue
      ? operator.getInputValue(filter.operator, filter.value)
      : { [filter.operator]: filter.value };
    if (filterDefinition.type === "boolean" && filter.key === "status") {
      branches.push({
        ruleEnabled: filter.value === true ? "ACTIVE" : "DISABLED",
      });
      continue;
    }
    branches.push(
      buildOrmFilterBranch(
        filter.key,
        lookup,
        ormFilterPlacement(filterDefinition, filter.key),
      ),
    );
  }

  const ormFilters =
    branches.length === 0
      ? undefined
      : branches.length === 1
        ? branches[0]
        : { all: branches };

  return {
    filter: ormFilters,
    order: sortableToOrmOrder(sortables),
  };
}

/**
 * Tab-locked filters + user filters. A tab's own filters are locked, so one
 * left behind by the tab before is dropped as locked; a user's filter on a
 * column the active tab sets gives way to the tab's. A user's filter on a
 * column some other tab sets - ``type`` on the documents' All tab - is theirs.
 */
function resolveConnectionFilters({
  tab,
  onChangeMap,
  filters,
}: {
  tab: string | null;
  onChangeMap: TabOnChangeMap;
  filters: FilterType[];
}): FilterType[] {
  const activeTabConfig = onChangeMap.find(
    (entry) => entry.when.type === "tab" && entry.when.value === tab,
  );
  const tabFilters = activeTabConfig ? activeTabConfig.set.value : [];
  const tabFilterKeys = new Set(tabFilters.map((filter) => filter.key));

  // Every column some tab set used to be dropped here too, to keep a tab's
  // filter from following the reader onto All - which ``isLocked`` already
  // does - so "invoices" asked for on All, by chip or in a sentence, reached
  // the URL and never the list.
  const userFilters = filters.filter(
    (filter) => !filter.isLocked && !tabFilterKeys.has(filter.key),
  );

  return [...tabFilters, ...userFilters];
}

export function useConnectionSetup<T>({
  appView,
  fetchPolicy,
  skip = false,
  minLoadingMs = DEFAULT_MIN_LOADING_MS,
}: {
  appView: ConnectionViewType;
  skip?: boolean;
  /**
   * How long to hold the skeleton before showing rows, in ms. Set 0 to render
   * as soon as the data is there.
   */
  minLoadingMs?: number;
  fetchPolicy?:
    | "network-only"
    | "cache-first"
    | "cache-and-network"
    | "no-cache"
    | "cache-only";
}) {
  // Widened from the registry's per-view literal type: this hook is generic
  // over every list view, so one query's concrete variables type would only
  // fight the shared variable builder.
  const { query, queryKey, typename, onChangeMap } = CONNECTION_VIEWS[
    appView
  ] as ConnectionViewConfig;
  const apolloClient = useApolloClient();
  const sortables = State((state) => state.sortables);
  const filters = State((state) => state.filters);
  const activeEntityIdentifiers = State(
    (state) => state.activeEntityIdentifiers,
  );
  // View and tab come from the route, not the store.
  //
  // The store's ``activeAppView``/``activeTab`` are synced from the URL by an
  // effect, so they lag it by a render — but React Router mounts this view
  // from that same URL, so on the first render the store still describes the
  // view we just left. That made ``effectiveFilters`` bail to ``[]`` and fire
  // one *unfiltered* query under a cache key nothing would ever read again,
  // before settling on the real variables. That stray request is what kept
  // hitting the network on a list that was already cached. The route is the
  // source of truth here and it is correct on the first render.
  const routeState = useAppRouteState();
  const activeTab = routeState.tab;
  const activeAppView = routeState.view;

  const columns = useMemo(() => toColumns(Objects[typename]), []);

  // The filter definitions are derived here, not read back from the store.
  // The effect below publishes them for the utility bar, but the *outgoing*
  // view's unmount clears that store to `{}` — so for the first renders after
  // navigating, every filter was silently skipped and the query went out
  // unfiltered, under a cache key nothing would ever read again and with a
  // `network-only` policy because that key was never fresh. That stray
  // request is what kept reloading an already-cached list. Until the route
  // and the store agree on the view, the list is asked with no filters.
  const isOwnView = appView === activeAppView;
  const queryVariables = useMemo(
    () =>
      connectionVariables(appView, {
        tab: isOwnView ? activeTab : null,
        filters: isOwnView ? filters : [],
        sortables,
        entityIdentifiers: activeEntityIdentifiers,
      }),
    [
      appView,
      isOwnView,
      activeTab,
      filters,
      sortables,
      activeEntityIdentifiers,
    ],
  );

  // Canonical form of the variables — the identity the cache and the freshness
  // window both key on, and what the min-loading window watches for changes.
  const variablesKey = canonicalStringify(queryVariables);

  // A constant fetch policy, deliberately.
  //
  // Deriving it from freshness (network-only when stale, cache-first when
  // warm) looked right but defeated itself: changing the ``fetchPolicy``
  // option makes Apollo reobserve the query, and the reobserve fetches — so
  // every switch back to an already-cached tab went to the network *because*
  // we had just decided it did not need to. With the option held constant,
  // ``cache-first`` does what it says and a complete cache entry is served
  // without a request. Expiry is handled below, by refetching explicitly.
  const effectiveFetchPolicy = fetchPolicy ?? "cache-first";

  const {
    data,
    loading: apolloLoading,
    fetchMore,
    refetch,
    networkStatus,
  } = useQuery(query, {
    variables: queryVariables,
    fetchPolicy: effectiveFetchPolicy,
    notifyOnNetworkStatusChange: true,
    skip,
  });

  // Start the freshness window from whenever this query's data landed.
  useEffect(() => {
    if (!data || apolloLoading) {
      return;
    }
    markFetched(query, queryVariables);
  }, [data, apolloLoading, query, variablesKey]);

  // Expire the cache after the freshness window.
  //
  // ``cache-first`` alone would serve a cached list forever, so this is the
  // other half of the policy: on each new set of variables, if the cache can
  // answer them but the answer is older than the window, show it and refresh
  // behind it. The completeness check is what keeps this from double-fetching
  // — a first-ever load is stale too, but its cache is empty, so Apollo is
  // already going to the network and a refetch here would just duplicate it.
  useEffect(() => {
    if (skip || isFresh(query, queryVariables)) {
      return;
    }
    const { complete } = apolloClient.cache.diff({
      query,
      variables: queryVariables,
      optimistic: true,
      returnPartialData: true,
    });
    if (!complete) {
      return;
    }
    // Mark before refetching so the landing result cannot re-trigger this.
    markFetched(query, queryVariables);
    void refetch();
    // Deliberately keyed on the variables alone: this is a once-per-variable-set
    // decision, and including `refetch`/`queryVariables` would re-run it on
    // identities that change every render.
  }, [variablesKey, skip]);

  // Minimum loading time logic
  const [isMinTimeElapsed, setIsMinTimeElapsed] = useState(false);
  const minLoadingKeyRef = useRef(variablesKey);

  // Close the window during render, not in an effect.
  //
  // Effects run after the commit, so arming there let the new rows paint
  // first and only then swapped in the skeleton — backwards, and the
  // expensive paint had already happened. Adjusting state during render makes
  // React re-render before it commits, so the rows never reach the screen
  // ahead of the skeleton. This is what makes the window work for a tab or
  // filter switch, where the component stays mounted and the rows change
  // underneath it.
  const variablesJustChanged = minLoadingKeyRef.current !== variablesKey;
  if (variablesJustChanged) {
    minLoadingKeyRef.current = variablesKey;
    if (isMinTimeElapsed) {
      setIsMinTimeElapsed(false);
    }
  }

  // Whenever the window is closed, open it again after ``minLoadingMs``.
  // Keyed on the flag itself, so re-arming is just closing it again, and the
  // cleanup cancels a pending timer on unmount or on a second change.
  useEffect(() => {
    if (isMinTimeElapsed) {
      return;
    }
    const timeout = setTimeout(() => setIsMinTimeElapsed(true), minLoadingMs);
    return () => clearTimeout(timeout);
  }, [isMinTimeElapsed, minLoadingMs]);

  // Calculate the effective loading state: true if apollo is loading OR the min time hasn't elapsed
  const loading = apolloLoading || !isMinTimeElapsed;
  const connection = data?.[queryKey]?.data;
  const connectionRows = (connection?.edges.map((edge) => ({
    ...edge.node,
  })) || []) as T[];
  const isRowsTransitioning =
    networkStatus === NetworkStatus.setVariables || !isMinTimeElapsed;
  const stableRowsRef = useRef<T[]>([]);
  if (variablesJustChanged) {
    // Drop the outgoing tab's rows. They are what ``rows`` falls back to
    // while the window is closed, so keeping them meant a tab switch showed
    // the *previous* list rather than a skeleton — and left ``totalCount``
    // non-zero, so the table never took its skeleton branch. This is the
    // whole reason the window appeared to do nothing on a tab toggle while
    // working on mount, where the ref happens to start empty.
    stableRowsRef.current = [];
  } else if (!isRowsTransitioning) {
    stableRowsRef.current = connectionRows;
  }
  const rows = isRowsTransitioning ? stableRowsRef.current : connectionRows;

  const totalCount =
    networkStatus === NetworkStatus.setVariables || !isMinTimeElapsed
      ? stableRowsRef.current.length > 0
        ? connection?.totalCount || stableRowsRef.current.length
        : 0
      : connection?.totalCount || 0;

  useLayoutEffect(() => {
    if (appView !== activeAppView) {
      return;
    }

    const resolved = resolveConnectionFilters({
      tab: activeTab,
      onChangeMap,
      filters: State.getState().filters,
    });

    if (!_.isEqual(resolved, State.getState().filters)) {
      State.setState({ filters: resolved });
    }
  }, [activeTab, activeAppView, appView, onChangeMap, filters]);

  useEffect(() => {
    // The definitions the list is asked with, so a chip the bar offers is
    // one the query compiles.
    const newFilterDefinitions = connectionFilterDefinitions(
      CONNECTION_VIEWS[appView] as ConnectionViewConfig,
    );

    const newSortableDefinitions = columnsToSortableDefinitions(columns);

    State.setState({
      filterDefinitions: newFilterDefinitions,
      sortableDefinitions: newSortableDefinitions,
    });
    return () => {
      State.setState({
        filterDefinitions: {},
        sortableDefinitions: {},
        refetch: () => null, // This refetch might need to be part of the slice's actions
      });
    };
  }, [appView, columns]);

  const { mutate } = useBackendMutation({
    mutation: Mutations.DELETE_NODES,
    callbacks: {
      onError: () => {
        void apolloClient.refetchQueries({ include: [query] });
      },
    },
  });

  const { mutate: requestBulkOperation } = useBackendMutation({
    mutation: Mutations.REQUEST_BULK_OPERATION,
  });

  const deleteNodeButtonProps = useMemo(() => {
    const name = displayNameForView(appView);
    return {
      variant: "destructive" as const,
      children: "Delete",

      // Every row the list matches, not only those loaded: deleted in the
      // background, and only once its asker has agreed to how many.
      acceptsAllMatching: true,

      confirm: ({ allMatching, count }: SelectionScope) =>
        allMatching
          ? `Delete all ${count.toLocaleString()} ${name.toLowerCase()} in this list? They'll be deleted in the background, and you'll get a notification when it's done.`
          : null,

      onClick: (
        checkedRows: string[],
        { allMatching, count }: SelectionScope,
      ) => {
        if (allMatching) {
          // What the list was asked with - so what it counted is what goes.
          void requestBulkOperation({
            variables: {
              input: {
                operation: BulkOperationEnum.Delete,
                name,
                connection: queryKey,
                variables: queryVariables,
                expectedCount: count,
              },
            },
            refetchQueries: [Queries.INBOX_CONNECTION_QUERY],
            onCompleted: () =>
              State.getState().setSnackbarMessage(
                `Deleting ${count.toLocaleString()} ${name.toLowerCase()}. You'll get a notification when it's done.`,
              ),
          });
          return;
        }
        removeNodesFromConnections(
          apolloClient.cache,
          queryKey,
          checkedRows,
          typename,
        );
        mutate({
          variables: {
            input: {
              ids: checkedRows,
            },
          },
          update: (cache) => {
            removeNodesFromConnections(cache, queryKey, checkedRows, typename);
          },
        });
      },
    };
  }, [
    apolloClient.cache,
    appView,
    mutate,
    queryKey,
    queryVariables,
    requestBulkOperation,
    typename,
  ]) as ComponentProps<typeof TableDialog>["buttons"][number];

  const loadNext = () =>
    fetchMore({
      variables: {
        first: 100,
        after: connection?.pageInfo.endCursor,
      },
    });

  const value = {
    rows: networkStatus === NetworkStatus.setVariables ? [] : rows,
    columns,
    loadNext,
    loading: loading || networkStatus === NetworkStatus.setVariables,
    totalCount,
    networkStatus,
    deleteNodeButtonProps,
    // A row created outside this query — the reports view makes one and opens
    // it straight away — is not in any cached page of the connection, so the
    // view that created it asks for the list again.
    refetch,
  };
  return value;
}
